import {
  parseAnswersMarkdown,
  trimDoi,
  type Block,
  type InlineNode,
  type ListItemNode,
} from './answersMarkdownParser';

import { MESSAGE_URL_PATTERN, toOpenableUrl } from '@/util/messageLinks';

/**
 * A Primum Specialist closes an answer with the papers behind it, as a block of text: a label, then
 * citations, each often followed by an `Available at:` line. This finds that block and reads it as
 * entries, so the app can draw it as a list where each paper is one tap from its page. Nothing
 * here changes the API: it works on the text of a reply.
 *
 * The match is deliberately cautious. A block is a reference list only when every entry in it
 * looks like a citation, so a sentence that happens to hold a year is never pulled out of the
 * answer.
 */
export type ReferenceEntry = {
  /** The citation as inline nodes, with no links of its own. */
  nodes: InlineNode[];
  /** Where the entry links to, or null when it carries no DOI, PMID or URL. */
  href: string | null;
  /** The link's own short text after the citation, "PubMed", "DOI" or the site, when it links. */
  label: string | null;
  /** True when `nodes` already hold the link, as the citation's own identifier. */
  inline?: boolean;
};

export type ReferenceSection = {
  /** The text before the list, with the label and the list removed. */
  body: string;
  entries: ReferenceEntry[];
  ordered: boolean;
  start: number;
};

const LABEL = new RegExp(
  '^\\s*(?:\\*\\*|__)?\\s*(?:selected |key |supporting )?(?:references?|sources?|citations?|bibliography|further reading|literature)\\s*:?\\s*(?:\\*\\*|__)?\\s*:?\\s*$',
  'i'
);

const AVAILABLE_AT = /^\s*available at\b:?\s*/i;

const MARKER = /^\s*(?:(\d{1,3})[.)]|[-*•●▪◦–])\s+/;

/** A citation is a line or two. Anything longer is the answer's own prose. */
const MAX_ENTRY = 900;

const ENTRY_PREFIX = /^(?:ref(?:erence)?s?\.?\s*\d*\s*[:.)]\s*)/i;

const YEAR = /\b(?:19|20)\d{2}\b/;

const AUTHOR = /\b[A-Z][A-Za-z'’-]+ [A-Z]{1,4}(?:[,.]| et al)|\bet al\b/;

const DOI_IN_TEXT = /\b10\.\d{4,9}\/[^\s<>"]+/;

const PMID_IN_TEXT = /\bPMID:?\s*(\d{1,9})\b/i;

const URL_IN_TEXT = new RegExp(MESSAGE_URL_PATTERN.source, 'i');

function hostOf(url: string): string {
  return toOpenableUrl(url)
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .split(/[/?#]/)[0];
}

/** Whether a run of text reads as one citation: authors and a year, or a pointer to the paper. */
function looksLikeCitation(text: string, lenient: boolean): boolean {
  if (text.length > MAX_ENTRY) {
    return false;
  }

  if (AVAILABLE_AT.test(text)) {
    return true;
  }

  if (lenient && (URL_IN_TEXT.test(text) || DOI_IN_TEXT.test(text) || PMID_IN_TEXT.test(text))) {
    return true;
  }

  return YEAR.test(text) && AUTHOR.test(text.slice(0, 120));
}

/**
 * Reads the entries of a reference block. A list marker, a blank line, or the line after an
 * `Available at:` or bare-link line starts a new entry. A line that stops mid-sentence is the
 * hard wrap of a long citation and joins the one above it.
 */
function groupEntries(region: string): { entries: string[]; ordered: boolean; start: number } {
  const entries: string[] = [];

  let ordered = false;

  let start = 1;

  let open = false;

  let closed = false;

  let afterBlank = false;

  const pointer = (line: string) =>
    AVAILABLE_AT.test(line) || /^\s*(?:https?:\/\/|www\.)\S+\s*$/i.test(line);

  for (const line of region.split('\n')) {
    if (line.trim() === '') {
      afterBlank = open;

      continue;
    }

    const marker = MARKER.exec(line);

    if (marker) {
      if (marker[1] !== undefined) {
        if (!ordered) {
          start = Number(marker[1]);
        }

        ordered = true;
      }

      entries.push(line.slice(marker[0].length).trim());

      open = true;

      closed = false;

      afterBlank = false;

      continue;
    }

    const text = line.trim();

    if (open && pointer(text)) {
      entries[entries.length - 1] += ` ${text}`;

      closed = true;

      afterBlank = false;

      continue;
    }

    const previous = entries[entries.length - 1] ?? '';

    const wrapped = open && !closed && !afterBlank && !/[.?!)\]\d]$/.test(previous);

    if (wrapped) {
      entries[entries.length - 1] += ` ${text}`;
    } else {
      entries.push(text);

      closed = pointer(text);
    }

    open = true;

    afterBlank = false;
  }

  return { entries, ordered, start };
}

function unlink(nodes: InlineNode[]): InlineNode[] {
  return nodes.flatMap((node): InlineNode[] => {
    if (node.kind === 'link') {
      return unlink(node.children);
    }

    return 'children' in node ? [{ ...node, children: unlink(node.children) }] : [node];
  });
}

function readEntry(raw: string): ReferenceEntry {
  const doiMatch = DOI_IN_TEXT.exec(raw);

  const doi = doiMatch ? trimDoi(doiMatch[0]) : null;

  const pmid =
    PMID_IN_TEXT.exec(raw)?.[1] ?? /pubmed\.ncbi\.nlm\.nih\.gov\/(\d{1,9})/i.exec(raw)?.[1] ?? null;

  const url = URL_IN_TEXT.exec(raw)?.[0] ?? null;

  const tail = new RegExp(
    `(?:\\s*available at\\b:?)?\\s*(?:${MESSAGE_URL_PATTERN.source})[\\s.;,]*$`,
    'i'
  );

  let shown = raw.replace(ENTRY_PREFIX, '').replace(tail, '').trim();

  if (shown === '') {
    shown = raw;
  }

  shown = shown.replace(/\s*available at\b:?\s*$/i, '').trim();

  const first = parseAnswersMarkdown(shown)[0];

  const nodes =
    first && first.kind !== 'list'
      ? unlink(first.children)
      : [{ kind: 'text' as const, text: shown }];

  let href: string | null = null;

  if (doi) {
    href = `https://doi.org/${doi}`;
  } else if (pmid) {
    href = `https://pubmed.ncbi.nlm.nih.gov/${pmid}/`;
  } else if (url) {
    href = toOpenableUrl(url);
  }

  return { nodes, href, label: href ? linkName(href) : null };
}

/** The entries of a block already known to be references. */
export function parseReferenceEntries(region: string): ReferenceSection | null {
  const lines = region.replace(/\r\n?/g, '\n').split('\n');

  while (lines.length > 0 && (lines[0].trim() === '' || LABEL.test(lines[0]))) {
    lines.shift();
  }

  const grouped = groupEntries(lines.join('\n'));

  if (
    grouped.entries.length === 0 ||
    !grouped.entries.every((entry) => looksLikeCitation(entry, true))
  ) {
    return null;
  }

  return {
    body: '',
    entries: grouped.entries.map(readEntry),
    ordered: grouped.ordered,
    start: grouped.start,
  };
}

/**
 * Splits a turn into the answer and the reference list closing it. The list is what follows the
 * last `References:` style label, or, with no label, a run of citations that ends the text. Null
 * when there is no list, or when anything in it does not read as a citation.
 */
export function splitReferenceSection(text: string): ReferenceSection | null {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');

  let labelAt = -1;

  lines.forEach((line, index) => {
    if (LABEL.test(line)) {
      labelAt = index;
    }
  });

  if (labelAt >= 0) {
    const region = lines.slice(labelAt + 1).join('\n');

    const section = parseReferenceEntries(region);

    if (section && section.entries.length > 0) {
      return { ...section, body: lines.slice(0, labelAt).join('\n').trim() };
    }

    return null;
  }

  return splitTrailingRun(lines);
}

function isCitationRun(region: string, lenient: boolean): boolean {
  const { entries } = groupEntries(region);

  return entries.length > 0 && entries.every((entry) => looksLikeCitation(entry, lenient));
}

function splitTrailingRun(lines: string[]): ReferenceSection | null {
  const chunks: { text: string; from: number }[] = [];

  let current: string[] = [];

  let from = 0;

  lines.forEach((line, index) => {
    if (line.trim() === '') {
      if (current.length > 0) {
        chunks.push({ text: current.join('\n'), from });
      }

      current = [];
    } else {
      if (current.length === 0) {
        from = index;
      }

      current.push(line);
    }
  });

  if (current.length > 0) {
    chunks.push({ text: current.join('\n'), from });
  }

  let first = chunks.length;

  while (first > 0) {
    const chunk = chunks[first - 1].text;

    const pointerOnly = AVAILABLE_AT.test(chunk) && !YEAR.test(chunk.split('\n')[0]);

    if (looksLikeCitation(chunk, false) || pointerOnly) {
      first -= 1;
    } else {
      break;
    }
  }

  while (first < chunks.length && AVAILABLE_AT.test(chunks[first].text)) {
    first += 1;
  }

  if (first >= chunks.length) {
    return null;
  }

  const region = lines.slice(chunks[first].from).join('\n');

  const section = parseReferenceEntries(region);

  if (!section || !isCitationRun(region, false)) {
    return null;
  }

  const hasPointer = section.entries.some((entry) => entry.href !== null);

  if (!hasPointer) {
    return null;
  }

  const body = lines.slice(0, chunks[first].from).join('\n').trim();

  return body === '' ? null : { ...section, body };
}

const NUMBERED = /^\s*\d{1,3}[.)]\s+/;

/**
 * An answer's references as the API serves them, as a section `referenceBlock` can draw. Each
 * citation links through its own identifier, the way AMA prints it, by `citationEntry`. The list is numbered when every citation was written with its number, which is dropped
 * from the text so it is not drawn twice.
 */
export function answerReferenceSection(
  references: { citation: string; url: string | null }[]
): ReferenceSection | null {
  const cited = references.filter((reference) => reference.citation.trim() !== '');

  if (cited.length === 0) {
    return null;
  }

  const ordered = cited.every((reference) => NUMBERED.test(reference.citation));

  const entries = cited.map(({ citation: text, url }) =>
    citationEntry(ordered ? text.replace(NUMBERED, '') : text, url)
  );

  return { body: '', entries, ordered, start: 1 };
}

/** The words of a citation without its formatting, for a link's spoken name. */
function plainText(nodes: InlineNode[]): string {
  return nodes
    .map((node) => {
      if (node.kind === 'text' || node.kind === 'code') {
        return node.text;
      }

      return node.kind === 'break' ? ' ' : plainText(node.children);
    })
    .join('')
    .trim();
}

/** The short text of a citation's link: "PubMed", "DOI", or the site it opens. */
function linkName(url: string): string {
  const host = hostOf(url);

  if (/(^|\.)pubmed\.ncbi\.nlm\.nih\.gov$/i.test(host)) {
    return 'PubMed';
  }

  return /(^|\.)doi\.org$/i.test(host) ? 'DOI' : host;
}

/**
 * One reference as a run of inline text: the citation with its own identifier already linked, or,
 * for a citation read from free text, the citation then one short link, "PubMed", "DOI" or the
 * site, where it has one.
 */
export function referenceItemNodes(entry: ReferenceEntry): InlineNode[] {
  if (entry.inline || !entry.href) {
    return entry.nodes;
  }

  const name = entry.label ?? linkName(entry.href);

  return [
    ...entry.nodes,
    { kind: 'text', text: ' ' },
    {
      kind: 'link',
      href: entry.href,
      // Heard as "PubMed: Alder K, ...", so a list of short links is told apart.
      label: `${name}: ${plainText(entry.nodes)}`,
      // A non-breaking space keeps "PMID:" on the line with its number.
      children: [{ kind: 'text', text: name.replace(': ', ':\u00a0') }],
    },
  ];
}

/**
 * The entries as one list block. Each citation reads as plain text, so a long AMA citation is not
 * a wall of underline, and ends in one short link where it has one.
 */
export function referenceBlock(section: ReferenceSection): Block {
  const items: ListItemNode[] = section.entries.map((entry) => ({
    children: referenceItemNodes(entry),
    blocks: [],
  }));

  return { kind: 'list', ordered: section.ordered, start: section.start, items };
}

/** What kind of source a reference is, for the summary on its folded section. */
export type ReferenceKind = 'article' | 'trial' | 'web';

const TRIAL_HOST = /(^|\.)clinicaltrials\.gov$/i;

const PAPER_HOST = /(^|\.)(pubmed\.ncbi\.nlm\.nih\.gov|ncbi\.nlm\.nih\.gov|doi\.org)$/i;

/**
 * A reference's kind, read from where it links: a ClinicalTrials.gov record is a trial, PubMed or a
 * DOI is an article, any other site is a web page. A citation with no link is read as an article,
 * unless it names a ClinicalTrials.gov identifier, since hand-written citations are papers.
 */
export function referenceKind(reference: { citation: string; url: string | null }): ReferenceKind {
  if (!reference.url) {
    return /clinicaltrials\.gov identifier/i.test(reference.citation) ? 'trial' : 'article';
  }

  const host = hostOf(reference.url);

  if (TRIAL_HOST.test(host)) {
    return 'trial';
  }

  return PAPER_HOST.test(host) ? 'article' : 'web';
}

const KIND_WORDS: Record<ReferenceKind, [string, string]> = {
  article: ['article', 'articles'],
  trial: ['trial', 'trials'],
  web: ['web page', 'web pages'],
};

/** "4 articles · 1 trial · 1 web page": what a folded list holds, kinds with none left out. */
export function referenceMix(references: { citation: string; url: string | null }[]): string {
  const counts: Record<ReferenceKind, number> = { article: 0, trial: 0, web: 0 };

  for (const reference of references) {
    counts[referenceKind(reference)] += 1;
  }

  return (Object.keys(KIND_WORDS) as ReferenceKind[])
    .filter((kind) => counts[kind] > 0)
    .map((kind) => {
      const [one, many] = KIND_WORDS[kind];

      return `${counts[kind]} ${counts[kind] === 1 ? one : many}`;
    })
    .join(' · ');
}

const ACCESSED = /\s*Accessed\s+[A-Z][a-z]+\.?\s+\d{1,2},\s+\d{4}\.?/gi;

const URL_ANYWHERE = new RegExp(MESSAGE_URL_PATTERN.source, 'gi');

const DOI_LABELLED = /\bdoi:\s*(10\.\d{4,9}\/[^\s<>"]+)/i;

const PMID_LABELLED = /\s*\bPMID:?\s*\d{1,9}\.?/gi;

const NCT_ID = /\bNCT\d{8}\b/i;

const PUBMED_URL_ID = /pubmed\.ncbi\.nlm\.nih\.gov\/(\d{1,9})/i;

/**
 * A citation with its locator taken off: any link, an `Available at:` label, and, unless kept, the
 * accessed date. The accessed date on a paper or a trial is the day the share was recorded, often
 * a backfill, not a day anyone read it, so it is dropped there and kept only for a web page, where
 * AMA needs it.
 */
function withoutLocator(text: string, keepAccessed: boolean): string {
  let shown = text.replace(URL_ANYWHERE, '').replace(/\s*available at\b:?/gi, '');

  if (!keepAccessed) {
    shown = shown.replace(ACCESSED, '');
  }

  return shown
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([.,;:])/g, '$1')
    .trim();
}

/** A run of citation text as inline nodes, keeping any emphasis but no links of its own. */
function inlineText(text: string): InlineNode[] {
  if (text === '') {
    return [];
  }

  const first = parseAnswersMarkdown(text)[0];

  return first && first.kind !== 'list' ? unlink(first.children) : [{ kind: 'text', text }];
}

/** How long a web link may print before it is cut, so a long URL does not run for lines. */
const SHORT_URL_MAX = 60;

/**
 * A web link as it prints in a citation: host and path with no scheme, `www.` or trailing slash,
 * cut with an ellipsis past `SHORT_URL_MAX` characters. The link still opens the full URL.
 */
export function shortUrl(url: string): string {
  const shown = toOpenableUrl(url)
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/$/, '');

  return shown.length > SHORT_URL_MAX ? `${shown.slice(0, SHORT_URL_MAX - 1)}…` : shown;
}

/** The citation text, then its identifier as the link, then whatever followed it. */
function linkedEntry(before: string, id: string, href: string, after = ''): ReferenceEntry {
  const lead = inlineText(before);

  const nodes: InlineNode[] = [
    ...lead,
    ...(lead.length > 0 ? [{ kind: 'text' as const, text: ' ' }] : []),
    {
      kind: 'link',
      href,
      // Heard as "Alder K, ..., opens PubMed", so an identifier is never spelled out and a list
      // of them is told apart.
      label:
        before === '' ? `Opens ${linkName(href)}` : `${plainText(lead)}, opens ${linkName(href)}`,
      // A non-breaking space keeps "PMID:" on the line with its number.
      children: [{ kind: 'text', text: id.replace(': ', ':\u00a0') }],
    },
    ...inlineText(after),
  ];

  return { nodes, href, label: id, inline: true };
}

/**
 * One citation as AMA prints it, with its identifier as the link, so every reference reads and
 * links the same way. A DOI comes first, printed `doi:10.1056/...` and opening doi.org. A paper
 * with no DOI ends in `PMID: 40293180`, opening PubMed. A trial links its NCT number where the
 * citation names it, opening ClinicalTrials.gov. Anything else ends in its URL, as AMA prints a
 * web page. A citation with nothing to link stays plain text.
 */
export function citationEntry(raw: string, url: string | null): ReferenceEntry {
  const text = raw.trim();

  const doi = DOI_LABELLED.exec(text);

  if (doi) {
    const id = trimDoi(doi[1]);

    return linkedEntry(
      withoutLocator(text.slice(0, doi.index), false),
      `doi:${id}`,
      `https://doi.org/${id}`
    );
  }

  const pmid = (url ? PUBMED_URL_ID.exec(url)?.[1] : undefined) ?? PMID_IN_TEXT.exec(text)?.[1];

  if (pmid) {
    return linkedEntry(
      withoutLocator(text.replace(PMID_LABELLED, ''), false),
      `PMID: ${pmid}`,
      `https://pubmed.ncbi.nlm.nih.gov/${pmid}/`
    );
  }

  const trialUrl = url && /clinicaltrials\.gov/i.test(url) ? url : null;

  const nct = NCT_ID.exec(trialUrl ?? '')?.[0] ?? NCT_ID.exec(text)?.[0];

  if (nct) {
    const shown = withoutLocator(text, false);

    const at = shown.search(new RegExp(`\\b${nct}\\b`, 'i'));

    const href = trialUrl ? toOpenableUrl(trialUrl) : `https://clinicaltrials.gov/study/${nct}`;

    if (at >= 0) {
      return linkedEntry(shown.slice(0, at).trimEnd(), nct, href, shown.slice(at + nct.length));
    }

    return linkedEntry(shown, nct, href);
  }

  if (url) {
    const href = toOpenableUrl(url);

    return linkedEntry(withoutLocator(text, true), shortUrl(url), href);
  }

  return readEntry(text);
}

/** The words on a reference's type badge. */
export const REFERENCE_KIND_LABELS: Record<ReferenceKind, string> = {
  article: 'Article',
  trial: 'Trial',
  web: 'Web page',
};

/**
 * A reference's type badge colors: blue for an article, orange for a trial, each a tint with its
 * deep text from the same brand group, and neutral gray for a web page. Green is left out on
 * purpose, since it reads as the promo and success tint (see `banners.md`). The pairs invert
 * between themes with the ramps, and `referenceBadgeContrast.test.ts` holds each to 4.5:1.
 */
export const REFERENCE_KIND_BADGE: Record<ReferenceKind, string> = {
  article: 'bg-secondary-50 text-secondary-800',
  trial: 'bg-tertiary-100 text-tertiary-800',
  web: 'bg-background-100 text-typography-800',
};
